import { z } from 'zod'

import { type ExamScheduleProgress, ExamTypeSchema } from '~/@entities/Exam'
import type { AllGradeSubject, SchoolType } from '~/@entities/SchoolSystem'
import {
  formatWorksheetScoringGrade,
  type ScoringItemInput,
  toScoringItem,
} from '~/@entities/Scoring'
import type { WorksheetScoringProblem } from '~/@entities/StudentWorksheet'
import type {
  CommonResponseListStudentExamWithExamResponse,
  CommonResponseStudentExamAssignmentResponse,
  ExamAwardResponse,
  ExamWithStudentExamResponse,
  StudentExamScoringSummaryResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'

import {
  type ExamAward,
  ExamAwardSchema,
  type ExamScoringSummary,
  ExamScoringSummarySchema,
  type ExamWithStudentExams,
  ExamWithStudentExamsSchema,
  type StudentExamAssignment,
  type StudentExamProblemScoring,
  StudentExamSchema,
  type StudentExamWithExam,
  StudentExamWithExamSchema,
} from './schemas'

const ENTITY = 'StudentExam'

export const adaptStudentExams = (
  raw: CommonResponseListStudentExamWithExamResponse['data'],
): StudentExamWithExam[] => parseApiResponse(z.array(StudentExamWithExamSchema), raw, ENTITY)

// 생성 타입의 StudentExamScoringSummaryResponse.problems 는 ProblemResponse 이름 충돌로 잡탕이다. 실제로는
// 시험 문항(adaptExamProblems)과 같은 문제 본체 V1 이 온다. 스키마가 실제 모양을 검증하므로 이 한 줄에서만
// unknown 을 거친다. 2026-10-13 백엔드 develop 의 StudentExamScoringSummaryResponse.kt 로 확인.
const asWire = (raw: StudentExamScoringSummaryResponse): unknown => raw

export const adaptExamScoringSummary = (
  raw: StudentExamScoringSummaryResponse,
): ExamScoringSummary => parseApiResponse(ExamScoringSummarySchema, asWire(raw), ENTITY)

export const adaptExamsWithStudentExams = (
  raw: ExamWithStudentExamResponse[],
): ExamWithStudentExams[] => parseApiResponse(z.array(ExamWithStudentExamsSchema), raw, ENTITY)

export const adaptExamAwards = (raw: ExamAwardResponse[]): ExamAward[] =>
  parseApiResponse(z.array(ExamAwardSchema), raw, ENTITY)

/***************** 서버 코드 표기의 번역표 *****************/
// 배정 상세(StudentExamAssignmentResponse)의 `examProgress` 는 ExamScheduleProgress.legacyProgress 로 한 칸씩
// 밀린 옛 진행 상태다 — legacy OPEN = 응시 중(IN_PROGRESS), legacy IN_PROGRESS = 채점 중(GRADING).
// DONE 은 schedule 매핑에서 나오지 않는 값이라 잠금이 유지되는 GRADING 으로 둔다. 도메인 어휘는 시험 본체가 쓰는
// scheduleProgress 하나이므로 코드로 오는 이 응답만 여기서 푼다.
// 2026-09-29 백엔드 develop exam/model/type/ExamScheduleProgress.kt 로 확인.
const EXAM_PROGRESS_CODES = [
  'READY',
  'PRE_OPEN',
  'OPEN',
  'IN_PROGRESS',
  'DONE',
  'RESOLVED',
] as const

const SCHEDULE_PROGRESS_BY_CODE = {
  READY: 'READY',
  PRE_OPEN: 'PRE_OPEN',
  OPEN: 'IN_PROGRESS',
  IN_PROGRESS: 'GRADING',
  DONE: 'GRADING',
  RESOLVED: 'RESOLVED',
} as const satisfies Record<(typeof EXAM_PROGRESS_CODES)[number], ExamScheduleProgress>

const StudentExamAssignmentWireSchema = StudentExamSchema.extend({
  examType: ExamTypeSchema,
  examProgress: z.enum(EXAM_PROGRESS_CODES),
  studentName: z.string(),
})

// 코드 → 도메인 값 번역은 검증 뒤의 순수 매핑이다. transform 안에 두면 lenient 모드에서 검증이 실패했을 때
// 코드 필드 그대로의 객체가 앱으로 흘러간다.
export const adaptStudentExamAssignment = (
  raw: CommonResponseStudentExamAssignmentResponse['data'],
): StudentExamAssignment => {
  const { examProgress, ...rest } = parseApiResponse(StudentExamAssignmentWireSchema, raw, ENTITY)
  return { ...rest, scheduleProgress: SCHEDULE_PROGRESS_BY_CODE[examProgress] }
}

export const toStudentExamProblemScoring = ({
  exam,
  problem,
  scoring,
}: {
  exam: {
    autoScorable: boolean
    label: string
    school?: SchoolType
    grade?: AllGradeSubject
  }
  problem: WorksheetScoringProblem
  scoring: ScoringItemInput<unknown, WorksheetScoringProblem>['record'] & {
    examProblemId: StudentExamProblemScoring['id']
    teacherScoringFlag: boolean
  }
}): StudentExamProblemScoring =>
  toScoringItem({
    target: {
      kind: 'EXAM',
      id: scoring.examProblemId,
      teacherScoringFlag: scoring.teacherScoringFlag,
    } as const,
    problem,
    record: scoring,
    content: {
      label: exam.label,
      autoScorable: exam.autoScorable,
      schoolType: exam.school,
      gradeLabel: formatWorksheetScoringGrade(exam.school, exam.grade),
    },
    answerLength: problem.answerCount,
  })
